import React, { useState } from "react";
import {
  Box,
  Container,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ClientMealPlanHeader from "./ClientMealPlanHeader";
import ClientWeeklyOverview from "./ClientWeeklyOverview";
import ClientDayView from "./ClientDayView";
import ClientShoppingList from "./ClientShoppingList";
import ClientRecipeDetails from "./ClientRecipeDetails";
import { useSmartShoppingList } from "../../../hooks/useSmartShoppingList";

/**
 * Main content component for client meal plan viewer
 * Provides tabbed navigation between weekly overview, daily views, and shopping list
 */
const ClientMealPlanContent = ({
  mealPlan,
  recipes,
  organizationBranding,
  weeklySchedule,
  ingredients,
  dailyNutritionTotals,
  organizationId,
  mealPlanId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activeTab, setActiveTab] = useState(0);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  // Fetch smart shopping list from API
  const { data: shoppingListData, isLoading: isLoadingShoppingList } = useSmartShoppingList(
    organizationId,
    mealPlanId,
  );

  // Use smart shopping list from API, fallback to local consolidation if needed
  const shoppingList = shoppingListData?.shoppingList || ingredients || [];

  // Ensure we have valid props for child components
  const safeWeeklySchedule = weeklySchedule || {};
  const safeDailyNutritionTotals = dailyNutritionTotals || {};
  const safeRecipes = recipes || [];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedDay(null);
    setSelectedRecipe(null);
  };

  const handleDaySelect = (day) => {
    setSelectedDay(day);
    setActiveTab(1); // Switch to day view tab
  };

  const handleRecipeSelect = (recipe) => {
    setSelectedRecipe(recipe);
  };

  const handleBackToWeekly = () => {
    setSelectedDay(null);
    setActiveTab(0);
  };

  const handleBackToDay = () => {
    setSelectedRecipe(null);
  };

  // If a specific recipe is selected, show recipe details
  if (selectedRecipe) {
    return (
      <Container maxWidth="lg">
        <ClientRecipeDetails recipe={selectedRecipe} onBack={handleBackToDay} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <ClientMealPlanHeader
        mealPlan={mealPlan}
        organizationBranding={organizationBranding}
        recipes={safeRecipes}
        weeklySchedule={safeWeeklySchedule}
      />

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 500,
            },
          }}
        >
          <Tab label="Weekly Overview" />
          <Tab
            label={
              selectedDay
                ? `${selectedDay.charAt(0).toUpperCase()}${selectedDay.slice(1)}`
                : "Daily View"
            }
          />
          <Tab label="Shopping List" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        {activeTab === 0 && (
          <ClientWeeklyOverview
            mealPlan={mealPlan}
            recipes={safeRecipes}
            onDaySelect={handleDaySelect}
            weeklySchedule={safeWeeklySchedule}
            dailyNutritionTotals={safeDailyNutritionTotals}
          />
        )}

        {activeTab === 1 && (
          <ClientDayView
            day={selectedDay || "monday"}
            mealPlan={mealPlan}
            recipes={safeRecipes}
            onRecipeSelect={handleRecipeSelect}
            onBackToWeekly={handleBackToWeekly}
            weeklySchedule={safeWeeklySchedule}
            dailyNutritionTotals={safeDailyNutritionTotals}
          />
        )}

        {activeTab === 2 && (
          <ClientShoppingList
            shoppingList={shoppingList}
            mealPlan={mealPlan}
            organizationId={organizationId}
            mealPlanId={mealPlanId}
            isLoading={isLoadingShoppingList}
            groupedByCategory={shoppingListData?.groupedByCategory}
          />
        )}
      </Box>
    </Container>
  );
};

export default ClientMealPlanContent;
